/* TargetDevicesListRenderer.js */

"use strict";
const Registrations = require("../../../../../../../RegistrationNamespaces");
const Grid = require("@gluenetworks/steve_ui_widgets").GridGl.Grid;
const AdvancedFilter = require("@gluenetworks/entity_grid_gl").AdvancedFilter;

class TargetDevicesListRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("target-devices-view-gl");
        this._rootElem.component = this._model;

        this._registerListeners();

        this._defaultViewActive = false;

        let columnConfig = {
            id: {
                label: "Id",
                width: 0.5
            },
            name: {
                label: "Name",
                width: 0.25
            },
            detectedHostName: {
                label: "Hostname",
                width: 0.15
            },
            ip: {
                label: "IP Address",
                width: 0.15
            },
            detectedSku: {
                label: "SKUs",
                width: 0.15
            },
            detectedOs: {
                label: "OS",
                width: 0.15
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                width: 0.15
            }
        };

        this._containerElement = document.createElement("div");
        this._containerElement.classList.add("target-container");

        this._gridContainerElem = document.createElement("div");
        this._gridContainerElem.classList.add("grid-container");

        this._titleElement = document.createElement("div");
        this._titleElement.classList.add("grid-title-element", "hidden");
        this._titleElement.innerText = "Filtered Devices";

        this._targetResultsGrid = new Grid({
            columns: columnConfig,
            showHeader: true,
            columnOrder: ["name", "detectedHostName", "ip", "detectedSku", "detectedOs", "detectedSoftwareVersion"],
            singleSelect: true
        });

        if (this._model._isMethodQuery) {
            this._queryContainer = document.createElement("div");
            this._queryContainer.classList.add("filter-element");
            this._containerElement.appendChild(this._queryContainer);
            this._containerElement.classList.add("filter-container");

            this._titleElement.classList.remove("hidden");
        }
        this._gridContainerElem.appendChild(this._titleElement);
        this._gridContainerElem.appendChild(this._targetResultsGrid.renderer.rootElement);

        this._containerElement.appendChild(this._gridContainerElem);
        this._gridRows = {};
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            resolve();
        });
        return prom;
    }

    _addViewActions() {
        this._model._gameEditor.addViewAction({
            type: "normal",
            displayName: "Back",
            name: "back",
            handler: this._onBackButtonClick.bind(this)
        });
    }

    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._containerElement);
        }
        this._model.whenModelIsReady().then(() => {
            if (this._model._isMethodQuery) {
                this._queryContainer.appendChild(this._renderQuery());
            }

            this._renderGridRows();
            this._addViewActions();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    _renderGridRows() {
        this._gridRows = {};
        this._gridRows = this._model.getTargetResultsRows();
        this._targetResultsGrid.setRows(this._gridRows);

        if (Object.keys(this._gridRows).length === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    _renderQuery() {
        const customFieldsList = [];

        for (let field in this._model._customFieldsWithInherited) {
            if (
                this._model._customFieldsWithInherited.hasOwnProperty(field) &&
                !this._model._customFieldsWithInherited[field].systemOwned &&
                ["Boolean", "String", "Enum", "Dropdown"].indexOf(this._model._customFieldsWithInherited[field].dataType) >= 0
            ) {
                customFieldsList.push(
                    Object.assign({}, this._model._customFieldsWithInherited[field], {
                        id: field,
                        name: `customFieldValues.${field}`,
                        label: this._model._customFieldsWithInherited[field].name
                    })
                );
            }
        }

        this._advancedFilter = new AdvancedFilter({
            filterData: {},
            columnsConfig: this._getFilterOptions(this._model._deviceEntityDef.$filterProperties, customFieldsList),
            allowGroups: true
        });
        this._advancedFilter.setQuery(this._model._query);

        return this._advancedFilter.renderer._rootElem;
    }

    _getFilterOptions(columnsData, customFields) {
        columnsData = columnsData.map((col) => Object.assign({}, { label: col.displayName }, col));
        const ipconfig = [
            {
                field: "connectionInformation.ip",
                id: "connectionInformation.ip",
                label: "IP Address",
                size: 35,
                dataType: "string",
                name: "connectionInformation.ip"
            }
        ];
        const updatedByField = [
            {
                field: "updatedBy",
                id: "updatedBy",
                label: "Updated By",
                dataType: "string",
                name: "updatedBy"
            }
        ];
        const filterOptions = [].concat(columnsData, customFields, ipconfig, updatedByField);
        const allowedCustomFieldTypes = ["boolean", "string", "enum"];
        return filterOptions
            .filter((option) => {
                if (option.dataType && !allowedCustomFieldTypes.includes(option.dataType.toLowerCase())) {
                    return false;
                }
                return true;
            })
            .map((optData) => {
                const options = {
                    dataType: optData.dataType ? optData.dataType.toLowerCase() : undefined
                };

                if (optData.dataType === "enum") {
                    options.options = [];
                    options.options = Object.keys(optData.labels).map((i) => ({ label: optData.labels[i], value: i }));
                }
                return Object.assign({}, optData, options);
            });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onBackButtonClick() {
        this._model._gameEditor.setActiveView(Registrations.AUDIT_POLICY_EDITOR_VIEW_HISTORY, [
            {
                value: {
                    fqn: Registrations.AUDIT_POLICY_EDITOR_SUBVIEW_TARGET_DEVICES_LIST
                }
            }
        ]);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _showDefaultMessageDisplay() {
        this._model.gameEditor
            .showDefaultMessageView({
                message: "No target devices"
            })
            .then(() => {
                this._defaultViewActive = true;
            });
    }

    _hideDefaultMessageDisplay() {
        this._model.gameEditor.hideDefaultMessageView().then(() => {
            this._defaultViewActive = false;
        });
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = TargetDevicesListRenderer;
